import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { cn } from "../lib/utils.js";
import { Sparkline } from "./sparkline.js";

interface KpiCardProps {
	label: string;
	value: string | number;
	unit?: string;
	delta?: string;
	deltaDir?: "up" | "down";
	foot?: string;
	featured?: boolean;
	sparkData?: number[];
	sparkColor?: string;
	className?: string;
}

export function KpiCard({
	label,
	value,
	unit,
	delta,
	deltaDir = "up",
	foot,
	featured = false,
	sparkData,
	sparkColor,
	className,
}: KpiCardProps) {
	return (
		<div
			className={cn(
				"relative flex min-h-[140px] flex-col justify-between overflow-hidden rounded-[14px] border p-5 pb-4",
				featured
					? "border-primary bg-primary text-primary-foreground"
					: "border-border bg-card text-foreground",
				className,
			)}
		>
			{/* Corner glow on featured */}
			{featured && (
				<div
					className="pointer-events-none absolute right-0 top-0 h-[120px] w-[120px]"
					style={{
						background:
							"radial-gradient(circle at top right, rgba(255,200,50,0.25), transparent 60%)",
					}}
				/>
			)}

			<div>
				<div
					className={cn(
						"flex items-center gap-2 text-[11.5px] font-medium uppercase tracking-[0.02em]",
						featured ? "text-primary-foreground/70" : "text-muted-foreground",
					)}
				>
					{featured && (
						<span className="inline-block h-1.5 w-1.5 rounded-full bg-secondary" />
					)}
					{label}
				</div>

				<div
					className={cn(
						"mt-3 text-[30px] font-semibold leading-none tracking-[-0.025em] tabular-nums",
						featured ? "text-primary-foreground" : "text-foreground",
					)}
				>
					{value}
					{unit && (
						<span
							className={cn(
								"ml-1 text-sm font-medium",
								featured
									? "text-primary-foreground/60"
									: "text-muted-foreground",
							)}
						>
							{unit}
						</span>
					)}
				</div>
			</div>

			<div className="mt-4 flex items-center justify-between text-xs">
				{delta !== undefined && (
					<span
						className={cn(
							"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-semibold tabular-nums",
							featured && deltaDir === "up"
								? "bg-[rgba(190,250,170,0.18)] text-[#BEFAAA]"
								: deltaDir === "up"
									? "bg-[rgba(127,184,122,0.18)] text-[#2E7A2A]"
									: "bg-[rgba(210,106,106,0.18)] text-[#9C3636]",
						)}
					>
						{deltaDir === "up" ? (
							<ArrowUpIcon className="h-[11px] w-[11px]" strokeWidth={2.5} />
						) : (
							<ArrowDownIcon className="h-[11px] w-[11px]" strokeWidth={2.5} />
						)}
						{delta}
					</span>
				)}
				{foot && (
					<span
						className={cn(
							"text-[11.5px]",
							featured ? "text-primary-foreground/60" : "text-muted-foreground",
						)}
					>
						{foot}
					</span>
				)}
			</div>

			{sparkData && (
				<Sparkline
					data={sparkData}
					color={
						sparkColor ??
						(featured ? "var(--color-secondary)" : "var(--color-primary)")
					}
					className="absolute bottom-[54px] right-[-1px] h-[40px] w-[100px]"
				/>
			)}
		</div>
	);
}
